import type { IScope } from "angular";
import { cloneDeep, isEmpty } from "lodash";

import type { IDataRetention, IResponseWithLoading, ISite } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditSiteDataPolicySettingsComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository"];

    public isNew: boolean;

    public bindings: { site: ISite; partnerDataRetention: IDataRetention };

    public original: IDataRetention;

    public edited: IDataRetention;

    public resolve: IFormContainerResolve<IDataRetention, { site: ISite; partnerDataRetention: IDataRetention }>;

    public editResponse: IResponseWithLoading<IDataRetention>;

    public deleteResponse: IResponseWithLoading<void>;

    constructor(private readonly $scope: IScope, private readonly sitesRepository: ISitesRepository) {}

    public $onInit() {
        this.isNew = isEmpty(this.resolve.formData);
        this.bindings = this.resolve.bindings;
        this.original =
            this.resolve.formData ||
            ({
                maxDaysCameraImages: this.bindings.partnerDataRetention.maxDaysCameraImages,
                maxDaysOtherAlarmVideo: this.bindings.partnerDataRetention.maxDaysOtherAlarmVideo,
                maxDaysRecordedVideo: this.bindings.partnerDataRetention.maxDaysRecordedVideo,
                maxDaysSharpAlarmVideo: this.bindings.partnerDataRetention.maxDaysSharpAlarmVideo,
            } as IDataRetention);

        this.edited = cloneDeep(this.original);
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editSiteDataPolicySettings() {
        const delta = PatchDelta.difference(this.original, this.edited);
        const observable = this.isNew
            ? this.sitesRepository.addDataRetention(this.bindings.site.id, this.edited)
            : this.sitesRepository.patchDataRetention(this.bindings.site.id, this.original.id, delta);

        if (!isEmpty(delta) || this.isNew) {
            extendWithLoadingAndFail(observable).subscribe(
                (response: IResponseWithLoading<IDataRetention>) => {
                    this.editResponse = response;
                    if (!response.isLoading && response.data) {
                        this.edited = response.data;
                        this.resolve.options.onOk(response.data);
                    }
                },
                (error: IResponseWithLoading<null>) => {
                    this.editResponse = error;
                },
            );
        } else {
            this.cancel();
        }
    }

    public deleteSiteDataRetentionSettings() {
        if (this.bindings.site.dataRetention) {
            extendWithLoadingAndFail(
                this.sitesRepository.deleteDataRetention(this.bindings.site.id, this.original.id),
            ).subscribe(
                (response) => {
                    this.deleteResponse = response;
                    if (!response.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
        } else {
            this.cancel();
        }
    }
}
